// Plausibility hints for the {{date}} value and the scheduled start. They warn,
// they do not block: a past date is normal in an incident ("seit 18.08.").
import { parseGermanDate } from "@/lib/enValues";

const WEEKDAY_EN = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const WEEKDAY_DE = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Warnings for a German date value: a day that does not exist, a weekday that
 * does not match, and (with `checkPast`, for maintenance) a day before today.
 * Without a year the date is read in the current year.
 */
export function dateWarnings(
  value: string,
  { checkPast, today = new Date() }: { checkPast: boolean; today?: Date },
): string[] {
  const parsed = parseGermanDate(value);
  if (!parsed) return [];
  const year = parsed.year ?? today.getFullYear();
  const date = new Date(year, parsed.month - 1, parsed.day);
  const pad = (n: number) => String(n).padStart(2, "0");
  const shown = `${pad(parsed.day)}.${pad(parsed.month)}.${year}`;
  if (date.getMonth() !== parsed.month - 1) return [`Den ${shown} gibt es nicht.`];
  const warnings: string[] = [];
  if (parsed.weekday && WEEKDAY_EN[date.getDay()] !== parsed.weekday) {
    const said = WEEKDAY_DE[WEEKDAY_EN.indexOf(parsed.weekday)];
    warnings.push(`Der ${shown} ist ein ${WEEKDAY_DE[date.getDay()]}, kein ${said}.`);
  }
  if (checkPast && date < startOfDay(today)) {
    warnings.push(
      parsed.year
        ? `Der ${shown} liegt in der Vergangenheit.`
        : `Der ${shown} liegt in der Vergangenheit (ohne Jahr als ${year} gelesen).`,
    );
  }
  return warnings;
}

/** A datetime-local value before now: the mail would go out at once, not later. */
export function isPastStart(value: string, now = new Date()): boolean {
  if (!value) return false;
  const start = new Date(value);
  return !Number.isNaN(start.getTime()) && start < now;
}
